import { getTableViewFromTableQuery } from 'owa-mail-triage-table-utils';
import {
    getFocusedInboxTableQuery,
    getOtherInboxTableQuery,
} from 'owa-mail-triage-table-utils/lib/getDefaultInboxTableQueries';
import onInitialTableLoadComplete from './onInitialTableLoadComplete';
import type { PerformanceDatapoint } from 'owa-analytics';
import { lazyLoadSearchTable } from 'owa-mail-execute-search-actions';
import loadInitialGroupTable from 'owa-mail-list-actions/lib/actions/table/loadInitialGroupTable';
import loadInitialMailTable from 'owa-mail-list-actions/lib/actions/table/loadInitialMailTable';
import loadInitialGroupFolderTable from 'owa-mail-list-actions/lib/actions/table/loadInitialGroupFolderTable';
import onLoadInitialRowsSucceeded from 'owa-mail-list-actions/lib/actions/table/onLoadInitialRowsSucceeded';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import type { ActionSource } from 'owa-mail-store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import getPersonaPhotoQueue from 'owa-persona/lib/actions/getPersonaPhotoQueue';
import { Module } from 'owa-workloads-module';
import { getCurrentModule } from 'owa-app-module-store';
import { action, orchestrator, mutatorAction } from 'satcheljs';
import type { TableQuery, TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import {
    getListViewState,
    type TableQueryType,
    getFocusedFilterForTable,
    type SortColumn,
    MailRowDataPropertyGetter,
    type LoadErrorStatus,
    isCopilotResultsTable,
    isCopilotResultsTabVisible,
    getPreviousTableViewId,
    isPriorityView,
    isMailListConversationView,
} from 'owa-mail-list-store';
import { onAfterTableLoad } from '../helpers/onAfterTableLoad';
import { resetSelection } from 'owa-mail-actions/lib/mailListSelectionActions';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { setModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getFolderChangeDigestAction } from 'owa-mail-focused-inbox-rollup-store';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { lazyExecuteGroupSearch } from 'owa-groups-execute-search-actions';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import type { SearchTableQuery } from 'owa-mail-list-search';
import { lazyLoadLocalMailSearchTable } from 'owa-mail-local-search';
import onAccountSwitched from 'owa-mail-account-actions/lib/actions/onAccountSwitched';
import { isSameCoprincipalAccountMailboxInfos } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getRowIdString } from 'owa-mail-list-store/lib/selectors/mailRowDataPropertyGetter';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import cleanupCopilotResultsTable from './cleanupCopilotResultsTable';

export interface SET_SELECTED_TABLEVIEWActionMessage {
    tableview: TableView;
    moduleContext: Module | undefined;
}
const setSelectedTableView = action(
    'SET_SELECTED_TABLEVIEW',
    (tableview: TableView, moduleContext?: Module): SET_SELECTED_TABLEVIEWActionMessage => ({
        tableview,
        moduleContext,
    })
);

export interface HANDLE_SELECTED_TABLEVIEWActionMessage {
    newTableView: TableView;
}
const handleFocusedOtherTables = action(
    'HANDLE_SELECTED_TABLEVIEW',
    (newTableView: TableView): HANDLE_SELECTED_TABLEVIEWActionMessage => ({
        newTableView,
    })
);

/**
 * Load the table from the table query
 * @param tableQuery the table query
 * @param loadTableViewDatapoint - performance datapoint that starts when the user initiates the table load
 * @param actionSource action that initiated the switch folder action
 * @param initialSessionData session data to be used for the initial load
 * @param moduleContext the module context for the load
 * @return a promise that resolves when the load table from server has completed
 */
export default async function loadTableViewFromTableQuery(
    tableQuery: TableQuery,
    loadTableViewDatapoint?: PerformanceDatapoint,
    actionSource?: ActionSource,
    initialSessionData?: SessionData,
    moduleContext?: Module,
    beforeLoadTask?: () => Promise<void>
): Promise<void> {
    const oldTableView = getSelectedTableView(moduleContext);
    const newTableView = getTableViewFromTableQuery(tableQuery);
    if (
        isGlobalFeatureEnabled('fwk-table-view-noop') &&
        oldTableView &&
        oldTableView === newTableView
    ) {
        // If the view is already selected, we don't need to do anything
        return Promise.resolve();
    }

    // Prevents reloading the table if the table is the same and tri-preserve-ml-selection is enabled
    // because nothing is required to change. Special-casing Route as it is used for initial navigation.
    // Also prevents reloading if the table is already loading and user selects the same table to avoid interrupting the load.
    if (
        actionSource !== 'Route' &&
        oldTableView &&
        oldTableView === newTableView &&
        (isGlobalFeatureEnabled('tri-preserve-ml-selection') || oldTableView.isLoading)
    ) {
        return Promise.resolve();
    }

    // Use the reference from the store (http://aka.ms/mobx4)
    tableQuery = newTableView.tableQuery;

    // If the user is navigating to a non-CopilotResults table while the CopilotResults tab is visible,
    // clean up the CopilotResults table only if we're navigating to a different folder.
    // Clicking the current folder or switching tabs (Focused/Other) within the same folder
    // should keep the CopilotResults tab intact.
    if (isCopilotResultsTabVisible() && !isCopilotResultsTable(tableQuery)) {
        const previousTableViewId = getPreviousTableViewId();
        const previousTableView = previousTableViewId
            ? getListViewStore().tableViews.get(previousTableViewId)
            : undefined;
        const previousFolderId = previousTableView?.tableQuery?.folderId;
        const isNavigatingToDifferentFolder =
            !previousFolderId || tableQuery.folderId !== previousFolderId;

        if (isNavigatingToDifferentFolder) {
            cleanupCopilotResultsTable('FolderSwitched');
        }
    }

    if ((newTableView.tableQuery as MailFolderTableQuery).scenarioType === 'priorityView') {
        setTableViewLoadingMutator(newTableView);
    }

    // 1. Set the current selected table in list view store to the new table
    setSelectedTableView(newTableView, moduleContext);
    const oldMailBox = getMailboxInfo(oldTableView);
    const currentMailBox = getMailboxInfo(newTableView);
    if (!isSameCoprincipalAccountMailboxInfos(oldMailBox, currentMailBox)) {
        onAccountSwitched(oldMailBox, currentMailBox);
    }
    // Store selectedRowKeys for old table before clearing the selection
    let selectedRowKeysForOldTable: string[] = [];
    let selectedRowIdForOldTable: string = '';

    // 2. Call onBeforeTableLoad before loading new table
    if (oldTableView) {
        // oldTableView is null when first loading OWA as there is no previously selected table
        selectedRowKeysForOldTable = [...oldTableView.selectedRowKeys.keys()];
        selectedRowIdForOldTable =
            getRowIdString(selectedRowKeysForOldTable[0], oldTableView) ?? '';

        // always clear the getPersonaPhotoQueue when switching the table
        getPersonaPhotoQueue.clear();

        if (
            !isGlobalFeatureEnabled('tri-preserve-ml-selection') ||
            isPriorityView(oldTableView.tableQuery as MailFolderTableQuery)
        ) {
            resetSelection(
                oldTableView,
                MailListItemSelectionSource.Reset,
                actionSource == 'FolderNodeClick'
            );
        }
    }

    // 3. Load New Table
    let loadTablePromise: Promise<void>;
    switch (tableQuery.type) {
        case 1:
            const searchTableQuery = tableQuery as SearchTableQuery;
            if (moduleContext === Module.Groups) {
                loadTablePromise = lazyExecuteGroupSearch.importAndExecute(
                    newTableView,
                    actionSource ?? '',
                    onInitialTableLoadComplete,
                    loadTableViewDatapoint
                );
            } else if (
                searchTableQuery.searchProvider === SearchProvider.Offline ||
                searchTableQuery.searchProvider === SearchProvider.PST ||
                searchTableQuery.searchProvider === SearchProvider.UnifiedInbox
            ) {
                loadTablePromise = lazyLoadLocalMailSearchTable.importAndExecute(
                    newTableView,
                    onInitialTableLoadComplete
                );
            } else {
                loadTablePromise = lazyLoadSearchTable.importAndExecute(
                    newTableView,
                    onInitialTableLoadComplete,
                    onLoadInitialRowsSucceeded,
                    actionSource ?? '',
                    currentMailBox,
                    loadTableViewDatapoint
                );
            }
            break;
        case 2:
            loadTablePromise = loadInitialGroupTable(
                newTableView,
                onInitialTableLoadComplete,
                actionSource ?? null,
                moduleContext
            );
            break;
        case 3:
            loadTablePromise = loadInitialGroupFolderTable(
                newTableView,
                onInitialTableLoadComplete,
                actionSource ?? null,
                moduleContext
            );
            break;
        case 0:
            if (beforeLoadTask) {
                await beforeLoadTask();
            }

            const shouldPreserveSelectionForSort = areTableQueriesSameExceptSortBy(
                oldTableView?.tableQuery as MailFolderTableQuery,
                newTableView.tableQuery as MailFolderTableQuery
            );

            // Determine if we need to preserve selection when sorting by Subject
            // when switching from conversation view to item view. This is a special
            // cased scenario based on the amount of user feedback we've received
            // when we didn't support this view. In the case of switching from conversation
            // to item view, we cannot preserve sort based on row ID as the row IDs
            // are different between conversation and item view. Hence, we need to
            // preserve selection based on matching subject string.
            const shouldPreserveSelectionForSubjectSort =
                selectedRowKeysForOldTable[0] &&
                areTableQueriesSameExceptSortBy(
                    oldTableView?.tableQuery as MailFolderTableQuery,
                    newTableView.tableQuery as MailFolderTableQuery,
                    false /* shouldCheckListViewType */
                ) &&
                (newTableView.tableQuery as MailFolderTableQuery)?.sortBy?.sortColumn === 8;

            // Determine if we need to preserve selection when switching FROM an item view table
            // TO another sort. The old row key is an item ID.
            // If the new table is conversation view, we need the conversation ID for that item.
            // If the new table is also item view, the item ID can be used directly.
            const shouldPreserveSelectionForFromItemViewTable =
                !isMailListConversationView(oldTableView) &&
                selectedRowKeysForOldTable[0] &&
                areTableQueriesSameExceptSortBy(
                    oldTableView?.tableQuery as MailFolderTableQuery,
                    newTableView.tableQuery as MailFolderTableQuery,
                    false /* shouldCheckListViewType */
                );

            // Load table at the selection if the table was previously loaded via
            // "jump to" (or using End key) otherwise the table will load from index 0
            // and the previously selected row may not be in the loaded range. Otherwise,
            // we'll have the previous contents in memory of the table and can load as normal.
            const selectedRowKeysForNewTable = [...newTableView.selectedRowKeys.keys()];
            const shouldLoadTableAtSelection =
                isGlobalFeatureEnabled('tri-preserve-ml-selection') &&
                selectedRowKeysForNewTable.length > 0 &&
                newTableView.loadedStartIndex !== 0;
            const selectedRowIdForNewTable = shouldLoadTableAtSelection
                ? getRowIdString(selectedRowKeysForNewTable[0], newTableView) ?? ''
                : '';

            const rowIdToLoad = getRowIdToLoad(
                !!shouldPreserveSelectionForSubjectSort,
                !!shouldPreserveSelectionForFromItemViewTable,
                shouldPreserveSelectionForSort,
                shouldLoadTableAtSelection,
                newTableView,
                oldTableView,
                selectedRowKeysForOldTable,
                selectedRowIdForOldTable,
                selectedRowIdForNewTable
            );

            loadTablePromise = loadInitialMailTable(
                newTableView,
                onInitialTableLoadComplete,
                actionSource ?? null,
                initialSessionData,
                rowIdToLoad,
                shouldPreserveSelectionForSubjectSort
                    ? {
                          matchingString: MailRowDataPropertyGetter.getSubject(
                              selectedRowKeysForOldTable[0],
                              oldTableView
                          ) /* Subject string to match */,
                          sortBy: (newTableView.tableQuery as MailFolderTableQuery)?.sortBy,
                      }
                    : undefined
            );
            break;
        case 4:
            // The Copilot Results table is prepopulated, so we just need to mark the load as complete
            onInitialTableLoadComplete(
                newTableView,
                true /* isSuccessfulResponseClass */,
                '' /* responseCode */,
                true /* isTablePrefetched */
            );

            loadTablePromise = Promise.resolve();
            break;
        default:
            throw new Error('UnsupportedTableQueryType');
    }

    // 4. Call onAfterTableLoad after loading new table
    if (oldTableView) {
        onAfterTableLoad(selectedRowKeysForOldTable, oldTableView, newTableView);
    }

    handleFocusedOtherTables(newTableView);

    return loadTablePromise;
}

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const setSelectedTableViewId = mutatorAction(
    'setSelectedTableViewId',
    (id: string, moduleContext?: Module) => {
        getListViewState(moduleContext).selectedTableViewId = id;
    }
);

orchestrator(setSelectedTableView, actionMessage => {
    const tableView = actionMessage.tableview;
    setSelectedTableViewId(tableView.id, actionMessage.moduleContext);
    if (tableView.tableQuery.type !== 1) {
        const currentModule = actionMessage.moduleContext ?? getCurrentModule() ?? Module.Mail;
        setModuleContextMailboxInfo(currentModule, getMailboxInfo(tableView));
    }
});

orchestrator(handleFocusedOtherTables, actionMessage => {
    const { newTableView } = actionMessage;

    const focusedViewFilter = getFocusedFilterForTable(newTableView);
    // If not a focused/other tableview, then return
    if (focusedViewFilter === -1) {
        return;
    }

    const newTableMailboxInfo = getMailboxInfo(newTableView);

    // Update on folder selection. Next updates are coming through notification. See: updateRollupOnAddOrUpdateRowNotification
    const tableQueryForFocusedOther =
        focusedViewFilter === 0
            ? getOtherInboxTableQuery(newTableMailboxInfo)
            : getFocusedInboxTableQuery(newTableMailboxInfo);
    getFolderChangeDigestAction(tableQueryForFocusedOther);
});

/**
 * Checks if two table queries are the same except for their sortBy property
 * @param oldTableQuery - The old table query to compare
 * @param newTableQuery - The new table query to compare
 * @param shouldCheckListViewType - Determines if we should compare ListViewType
 * @returns true if all properties except sortBy are the same, false otherwise
 */
function areTableQueriesSameExceptSortBy(
    oldTableQuery: MailFolderTableQuery | undefined,
    newTableQuery: MailFolderTableQuery | undefined,
    shouldCheckListViewType: boolean = true
): boolean {
    if (!oldTableQuery || !newTableQuery) {
        return false;
    }

    // This is an optional check so that we can preserve selection after between
    // a sort that goes from conversation view  to item view if a sort is ONLY
    // supported in item view (i.e. Date sort to Subject sort).
    if (shouldCheckListViewType && oldTableQuery.listViewType !== newTableQuery.listViewType) {
        return false;
    }

    // Compare all properties from TableQuery interface
    if (
        oldTableQuery.folderId !== newTableQuery.folderId ||
        oldTableQuery.type !== newTableQuery.type
    ) {
        return false;
    }

    // Compare all properties from MailFolderTableQuery interface except sortBy
    if (
        oldTableQuery.categoryName !== newTableQuery.categoryName ||
        oldTableQuery.focusedViewFilter !== newTableQuery.focusedViewFilter ||
        oldTableQuery.messageSenderScreeningFilter !== newTableQuery.messageSenderScreeningFilter ||
        oldTableQuery.scenarioType !== newTableQuery.scenarioType ||
        oldTableQuery.viewFilter !== newTableQuery.viewFilter
    ) {
        return false;
    }

    // Only return true if sortBy properties are different to ensure that the value
    // returned is true only when the two table queries are the same except for sortBy
    // (i.e. return false if all properties including sortBy are the same).
    return (
        oldTableQuery.sortBy?.sortColumn !== newTableQuery.sortBy?.sortColumn ||
        oldTableQuery.sortBy?.sortDirection !== newTableQuery.sortBy?.sortDirection
    );
}

const setTableViewLoadingMutator = mutatorAction('setTableViewLoading', (tableView: TableView) => {
    tableView.isInitialLoadComplete = false;
    tableView.loadErrorStatus = 0;
});

/**
 * Determines which row ID to pass to loadInitialMailTable so that the table scrolls to and
 * re-selects the correct row after a sort or view-type change.
 *
 * Priority order (first match wins, later conditions are only reached when earlier ones are false):
 *
 * 1. Subject-sort preservation  → undefined
 *    The caller will handle selection via a subject-string match instead of a row ID, so we
 *    must not supply a rowIdToLoad (it would conflict with the subject-match logic).
 *
 * 2. From-item-view preservation → conversation ID or item ID
 *    When the old table was in item view and we're changing sort, row IDs may differ from
 *    conversation IDs. If the new table is conversation view we need the conversation ID for
 *    the selected item; if it's also item view the item ID can be reused directly.
 *
 * 3. Same-view-type sort preservation → old table's selected row ID
 *    When only the sort column/direction changed and the view type is the same, the row ID is
 *    stable across the reload so we can scroll directly to it.
 *
 * 4. Load-at-selection (e.g. after "jump to" or End key) → new table's selected row ID
 *    The table was previously loaded at a non-zero start index. We must supply the row ID so
 *    the server-side page fetch is anchored at the right position.
 *
 * 5. Default → undefined
 *    No special anchor needed; the table loads from index 0.
 */
function getRowIdToLoad(
    shouldPreserveSelectionForSubjectSort: boolean,
    shouldPreserveSelectionForFromItemViewTable: boolean,
    shouldPreserveSelectionForSort: boolean,
    shouldLoadTableAtSelection: boolean,
    newTableView: TableView,
    oldTableView: TableView,
    selectedRowKeysForOldTable: string[],
    selectedRowIdForOldTable: string,
    selectedRowIdForNewTable: string
): string | undefined {
    // Case 1: Subject-sort match will handle selection — no row ID anchor needed.
    if (shouldPreserveSelectionForSubjectSort) {
        return undefined;
    }

    // Case 2: Old table was item view; resolve to the correct ID type for the new table.
    if (shouldPreserveSelectionForFromItemViewTable) {
        return isMailListConversationView(newTableView)
            ? MailRowDataPropertyGetter.getConversationId(
                  selectedRowKeysForOldTable[0],
                  oldTableView
              )
            : selectedRowIdForOldTable;
    }

    // Case 3: Only the sort changed (same view type) — row ID is stable.
    if (shouldPreserveSelectionForSort) {
        return selectedRowIdForOldTable;
    }

    // Case 4: Table was previously loaded at a non-zero offset (e.g. "jump to").
    if (shouldLoadTableAtSelection) {
        return selectedRowIdForNewTable;
    }

    return undefined;
}
